'use client';

import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

/**
 * Centralized MUI theme that uses CSS custom properties from the design system.
 * This theme automatically adapts when the theme class changes on the HTML element
 * (e.g., from light to dark mode).
 *
 * The CSS variables are defined in:
 * - packages/design-system/src/css/themes/light.css
 * - packages/design-system/src/css/themes/dark.css
 * - packages/design-system/src/css/themes/high-contrast.css
 * - packages/design-system/src/css/themes/dark-high-contrast.css
 */

// Extended palette type to include channel tokens.
// MUI cannot derive channels from var() values, so we provide them
// via CSS variables defined in channel-tokens.css.
type PaletteWithChannels = ThemeOptions['palette'] & {
  primaryChannel?: string;
  secondaryChannel?: string;
};

const themeOptions: ThemeOptions = {
  cssVariables: {
    // nativeColor allows using CSS custom properties (var(--color-*)) in palette
    nativeColor: true,
  },
  palette: {
    mode: 'light',
    primary: {
      main: 'var(--color-primary)',
      contrastText: 'var(--color-on-primary)',
    },
    secondary: {
      main: 'var(--color-secondary)',
      contrastText: 'var(--color-on-secondary)',
    },
    error: {
      main: 'var(--color-error)',
      contrastText: 'var(--color-on-error)',
    },
    warning: {
      main: 'var(--color-warning)',
      contrastText: 'var(--color-on-warning)',
    },
    success: {
      main: 'var(--color-success)',
      contrastText: 'var(--color-on-success)',
    },
    background: {
      default: 'var(--color-background)',
      paper: 'var(--color-surface)',
    },
    text: {
      primary: 'var(--color-on-surface)',
      secondary: 'var(--color-on-surface-variant)',
      // MUI's default is `rgba(0, 0, 0, 0.38)` — a BLACK alpha, picked from
      // `palette.mode`, which is pinned to 'light' above because themes switch
      // via the `.dark` class rather than this flag. Under `.dark` that put
      // black text on a near-black surface: 1.07:1, effectively invisible
      // (2.66:1 in light, also under the 4.5:1 minimum). Mixing the theme's own
      // foreground token instead flips with the theme like the two above.
      disabled: 'color-mix(in srgb, var(--color-on-surface) 45%, transparent)',
    },
    action: {
      active: 'var(--color-on-surface-variant)',
      // MUI derives hover/selected overlays from `palette.mode`, which is
      // hardcoded to 'light' above (theme switching happens via the `.dark`
      // CSS class, not this flag) — so the default overlays are a
      // near-invisible black tint on dark backgrounds. Pin them to a token
      // that already flips per theme instead.
      hover: 'var(--color-surface-variant)',
      selected: 'var(--color-primary-container)',
    },
    divider: 'var(--color-outline-variant)',
    // Channel tokens from CSS variables (space-separated RGB).
    // Defined in channel-tokens.css, not in auto-generated theme files.
    primaryChannel: 'var(--color-primary-channel)',
    secondaryChannel: 'var(--color-secondary-channel)',
  } as PaletteWithChannels,
  typography: {
    fontFamily: 'var(--font-roboto), Roboto, sans-serif',
    button: {
      textTransform: 'none',
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.02857em',
    },
    h1: {
      fontSize: 'var(--text-display-large)',
      lineHeight: 'var(--text-display-large--line-height)',
    },
    h2: {
      fontSize: 'var(--text-display-medium)',
      lineHeight: 'var(--text-display-medium--line-height)',
    },
    h3: {
      fontSize: 'var(--text-display-small)',
      lineHeight: 'var(--text-display-small--line-height)',
    },
    h4: {
      fontSize: 'var(--text-headline-large)',
      lineHeight: 'var(--text-headline-large--line-height)',
    },
    h5: {
      fontSize: 'var(--text-headline-medium)',
      lineHeight: 'var(--text-headline-medium--line-height)',
    },
    h6: {
      fontSize: 'var(--text-headline-small)',
      lineHeight: 'var(--text-headline-small--line-height)',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'var(--color-background)',
          color: 'var(--color-on-surface)',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: 'var(--color-on-surface)',
          '&.Mui-disabled': {
            color: 'var(--color-on-surface-variant)',
            WebkitTextFillColor: 'var(--color-on-surface-variant)',
          },
        },
        input: {
          color: 'var(--color-on-surface)',
          '&::placeholder': {
            color: 'var(--color-on-surface-variant)',
            opacity: 1,
          },
          '&.Mui-disabled': {
            color: 'var(--color-on-surface-variant)',
            WebkitTextFillColor: 'var(--color-on-surface-variant)',
          },
          // Fix autofill styles for dark theme
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
            {
              WebkitBoxShadow: '0 0 0 1000px var(--color-surface) inset !important',
              WebkitTextFillColor: 'var(--color-on-surface) !important',
              caretColor: 'var(--color-on-surface)',
              transition: 'background-color 5000s ease-in-out 0s',
            },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'var(--color-outline)',
        },
        root: {
          color: 'var(--color-on-surface)',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-outline)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-primary)',
          },
          '&.Mui-disabled': {
            opacity: 0.7,
            color: 'var(--color-on-surface-variant)',
            WebkitTextFillColor: 'var(--color-on-surface-variant)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-outline-variant)',
            },
          },
        },
        input: {
          color: 'var(--color-on-surface)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--color-on-surface-variant)',
          '&.Mui-focused': {
            color: 'var(--color-primary)',
          },
          '&.Mui-disabled': {
            color: 'var(--color-on-surface-variant)',
            opacity: 0.7,
          },
        },
        asterisk: {
          '&::before': {
            content: '" "',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-disabled': {
            backgroundColor: 'var(--color-surface-variant)',
            color: 'var(--color-on-surface-variant)',
            opacity: 0.6,
          },
        },
        contained: {
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-on-primary)',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'var(--color-primary)',
            filter: 'brightness(1.15)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-on-primary)',
            opacity: 0.5,
          },
        },
        outlined: {
          borderColor: 'var(--color-outline)',
          color: 'var(--color-primary)',
          '&:hover': {
            borderColor: 'var(--color-primary)',
            backgroundColor: 'var(--color-surface-variant)',
          },
        },
        text: {
          color: 'var(--color-primary)',
          '&:hover': {
            backgroundColor: 'var(--color-surface-variant)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'var(--color-outline)',
          '&.Mui-checked': {
            color: 'var(--color-primary)',
          },
          '&.Mui-disabled': {
            color: 'var(--color-outline)',
            opacity: 0.5,
          },
          '&.Mui-checked.Mui-disabled': {
            color: 'var(--color-primary)',
            opacity: 0.5,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.7,
          },
        },
        label: {
          '&.Mui-disabled': {
            color: 'var(--color-on-surface-variant)',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: 'var(--color-outline)',
          '&.Mui-checked': {
            color: 'var(--color-primary)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: 'var(--color-primary)',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'var(--color-primary)',
          },
        },
        track: {
          backgroundColor: 'var(--color-outline)',
          opacity: 1,
        },
        thumb: {
          color: 'var(--color-on-surface)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.Mui-disabled': {
            color: 'var(--color-on-surface-variant)',
            WebkitTextFillColor: 'var(--color-on-surface-variant)',
          },
        },
        icon: {
          color: 'var(--color-on-surface-variant)',
          '&.Mui-disabled': {
            color: 'var(--color-on-surface-variant)',
            opacity: 0.5,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: 'var(--color-on-surface-variant)',
          '&.Mui-disabled': {
            color: 'var(--color-on-surface-variant)',
            opacity: 0.7,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'var(--color-primary)',
          textDecoration: 'none',
          transition: 'color 0.2s ease-in-out',
          '&:hover': {
            color: 'var(--color-primary-container)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-surface-variant)',
          '& .MuiTableCell-head': {
            color: 'var(--color-on-surface)',
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'var(--color-outline-variant)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-surface-variant)',
          color: 'var(--color-on-surface-variant)',
        },
        colorPrimary: {
          backgroundColor: 'var(--color-primary-container)',
          color: 'var(--color-on-primary-container)',
        },
        colorSecondary: {
          backgroundColor: 'var(--color-secondary-container)',
          color: 'var(--color-on-secondary-container)',
        },
        colorError: {
          backgroundColor: 'var(--color-error-container)',
          color: 'var(--color-on-error-container)',
        },
        colorSuccess: {
          backgroundColor: 'var(--color-success-container)',
          color: 'var(--color-on-success-container)',
        },
        colorWarning: {
          backgroundColor: 'var(--color-warning-container)',
          color: 'var(--color-on-warning-container)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--color-surface-variant)',
          color: 'var(--color-on-surface-variant)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--color-surface)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--color-surface)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          '&.Mui-selected': {
            fontWeight: 400,
          },
          '&.Mui-selected:hover': {
            fontWeight: 400,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
        },
      },
    },
  },
};

export const muiTheme: Theme = createTheme(themeOptions);

export default muiTheme;
